import { all, call, put, takeLatest } from 'redux-saga/effects';
import { Action } from 'typescript-fsa';

import { hideLoader, showLoader } from '@topconagriculture/loader';
import { Action as PermissionAction, Resource } from '@topconagriculture/permissions';
import { checkPermissions } from '@topconagriculture/casbin-react';

import api from '../../../core/api';
import * as actions from './zones.actions';
import { notificationError, notificationSuccess } from '../../../core/helpers/utils/notificationUtils';
import { composeErrorMessage } from '../../../core/helpers/utils/utils';
import { LoadersNames } from '../../ui/ui.constants';
import { compose403ErrorMessage, successHandler } from '../../../core/helpers/utils/sagaUtils';
import { getMixersList } from '../mixers/mixers.actions';

export function* getZonesListSaga() {
  yield put(showLoader(LoadersNames.fetchZones));
  try {
    const canReadZones = yield call(checkPermissions, {
      resource: Resource.FEED_MANAGE_ZONE_CONTROL,
      action: PermissionAction.READ,
    });
    if (canReadZones) {
      const result = yield call(api.zones.getZonesList, true);
      yield put(
        actions.getZonesList.done({
          params: {},
          result,
        }),
      );
    }
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchZones));
}

export function* getZoneSaga(action: Action<{ id: number }>) {
  try {
    const result = yield call(api.zones.getZone, action.payload.id);

    yield put(
      actions.getZoneById.done({
        params: {},
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
}

export function* createZoneSaga(action: Action<any>) {
  const { description, zoneNumber, includedGroups, includedMixers, formName } = action.payload;

  try {
    const zonePayload = {
      description,
      zoneNumber,
      groupIds: includedGroups.map((x) => x.id),
      mixerIds: includedMixers.map((x) => x.id),
    };
    const zone = yield call(api.zones.createZone, zonePayload, formName);

    yield put(
      actions.createZone.done({
        params: {},
        result: zone,
      }),
    );

    successHandler(action.meta);
    yield put(notificationSuccess('zones:create_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

export function* editZoneSaga(action: Action<any>) {
  yield put(showLoader(LoadersNames.editZone));
  const { description, zoneNumber, id, includedGroups, includedMixers, formName } = action.payload;
  try {
    const payload = {
      id,
      description,
      zoneNumber,
      groupIds: includedGroups.map((group) => group.id),
      mixerIds: includedMixers.map((mixer) => mixer.id),
    };
    const zone = yield call(api.zones.updateZone, payload, formName);

    yield put(
      actions.updateZone.done({
        params: {},
        result: zone,
      }),
    );

    successHandler(action.meta);
    yield put(notificationSuccess('zones:update_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.editZone));
}

export function* deleteZoneSaga(action: ReturnType<typeof actions.deleteZone.started>) {
  try {
    const { id, formName } = action.payload;
    const result = yield call(api.zones.deleteZone, id, formName);

    yield put(
      actions.deleteZone.done({
        params: action.payload,
        result,
      }),
    );
    successHandler(action.meta);
    yield put(notificationSuccess('zones:delete_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

export function* watchZonesSaga() {
  yield all([
    takeLatest(getMixersList.done, getZonesListSaga),
    takeLatest(actions.getZonesList.started, getZonesListSaga),
    takeLatest(actions.getZoneById.started, getZoneSaga),
    takeLatest(actions.createZone.started, createZoneSaga),
    takeLatest(actions.updateZone.started, editZoneSaga),
    takeLatest(actions.deleteZone.started, deleteZoneSaga),
  ]);
}
